import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Modal, Button, Badge, Alert } from 'react-bootstrap';
import AlertContext from '../../context/AlertContext';
import Sidedash from '../uicomponents/Sidedash';

// CSS styles for sortable headers
const sortableHeaderStyles = `
    .sortable-header {
        transition: background-color 0.2s ease;
        user-select: none;
        position: relative;
    }
    .sortable-header:hover {
        background-color: rgba(0, 123, 255, 0.1) !important;
    }
    .sortable-header:active {
        background-color: rgba(0, 123, 255, 0.2) !important;
    }
    .sortable-header i {
        margin-left: 5px;
        vertical-align: middle;
    }
`;

// Utility function to format bytes as human-readable string
function formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

const Media = () => {
    const host = process.env.REACT_APP_BACKEND_URL;
    const { showAlert } = useContext(AlertContext);

    // State management
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({});
    const [currentFilter, setCurrentFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);

    // Sorting state
    const [sortField, setSortField] = useState('uploadedAt');
    const [sortOrder, setSortOrder] = useState('desc');

    // Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [syncResults, setSyncResults] = useState(null);

    // Operation states
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [processingFiles, setProcessingFiles] = useState(new Set());

    const fetchMedia = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: currentPage,
                limit: 50,
                filter: currentFilter,
                search: search,
                sortField: sortField,
                sortOrder: sortOrder
            });

            const response = await fetch(`${host}/media/list?${params}`, {
                headers: {
                    'auth-token': localStorage.getItem('token')
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch media');
            }

            const data = await response.json();
            setMedia(data.media);
            setTotalPages(data.totalPages);
            setFilters(data.filters);
        } catch (error) {
            console.error('Error fetching media:', error);
            showAlert('Failed to fetch media files', 'danger');
        } finally {
            setLoading(false);
        }
    }, [currentPage, currentFilter, search, sortField, sortOrder, host, showAlert]);

    const fetchStats = useCallback(async () => {
        try {
            const response = await fetch(`${host}/media/stats`, {
                headers: {
                    'auth-token': localStorage.getItem('token')
                }
            });

            if (response.ok) {
                const data = await response.json();
                setStats(data);
                // Add human readable sizes for stats
                data.usedSizeHuman = formatBytes(data.usedSize || 0);
                data.markedSizeHuman = formatBytes(data.markedSize || 0);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, [host]);

    useEffect(() => {
        fetchMedia();
        fetchStats();
    }, [fetchMedia, fetchStats]);

    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle sort order if same field
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field with default ascending order
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1); // Reset to first page when sorting
    };

    const getSortIcon = (field) => {
        if (sortField !== field) {
            return <i className="fas fa-sort text-muted" style={{ fontSize: '12px' }}></i>;
        }
        return sortOrder === 'asc' 
            ? <i className="fas fa-sort-up text-primary" style={{ fontSize: '12px' }}></i>
            : <i className="fas fa-sort-down text-primary" style={{ fontSize: '12px' }}></i>;
    };

    const handleMarkForDeletion = async (fileIds = selectedFiles) => {
        if (fileIds.length === 0) {
            showAlert('Please select files to mark for deletion', 'warning');
            return;
        }

        // Add files to processing set
        setProcessingFiles(prev => new Set([...prev, ...fileIds]));
        
        try {
            const response = await fetch(`${host}/media/mark-for-deletion`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('token')
                },
                body: JSON.stringify({
                    fileIds: fileIds,
                    reason: 'manual',
                    daysDelay: 7
                })
            });

            const data = await response.json();
            
            if (response.ok) {
                // Optimistically update the media state
                setMedia(prevMedia => 
                    prevMedia.map(file => 
                        fileIds.includes(file._id) 
                            ? { ...file, markedForDeletion: true, deletionDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) }
                            : file
                    )
                );
                
                showAlert(`${data.markedCount} files marked for deletion`, 'success');
                setSelectedFiles([]);
                fetchStats(); // Only refresh stats, not the entire media list
            } else {
                showAlert(data.error || 'Failed to mark files for deletion', 'danger');
            }
        } catch (error) {
            console.error('Error marking files for deletion:', error);
            showAlert('Failed to mark files for deletion', 'danger');
        } finally {
            // Remove files from processing set
            setProcessingFiles(prev => {
                const newSet = new Set(prev);
                fileIds.forEach(id => newSet.delete(id));
                return newSet;
            });
        }
    };

    const handleUnmarkForDeletion = async (fileIds = selectedFiles) => {
        if (fileIds.length === 0) {
            showAlert('Please select files to unmark for deletion', 'warning');
            return;
        }

        // Add files to processing set
        setProcessingFiles(prev => new Set([...prev, ...fileIds]));
        
        try {
            const response = await fetch(`${host}/media/unmark-for-deletion`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('token')
                },
                body: JSON.stringify({
                    fileIds: fileIds
                })
            });

            const data = await response.json();
            
            if (response.ok) {
                // Optimistically update the media state
                setMedia(prevMedia => 
                    prevMedia.map(file => 
                        fileIds.includes(file._id) 
                            ? { ...file, markedForDeletion: false, deletionDate: null, deletionReason: null }
                            : file
                    )
                );
                
                showAlert(`${data.unmarkedCount} files unmarked for deletion`, 'success');
                setSelectedFiles([]);
                fetchStats(); // Only refresh stats, not the entire media list
            } else {
                showAlert(data.error || 'Failed to unmark files for deletion', 'danger');
            }
        } catch (error) {
            console.error('Error unmarking files for deletion:', error);
            showAlert('Failed to unmark files for deletion', 'danger');
        } finally {
            // Remove files from processing set
            setProcessingFiles(prev => {
                const newSet = new Set(prev);
                fileIds.forEach(id => newSet.delete(id));
                return newSet;
            });
        }
    };

    const handleDeleteMarked = async () => {
        setIsDeleting(true);
        try {
            const response = await fetch(`${host}/media/delete-marked`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('token')
                }
            });

            const data = await response.json();
            
            if (response.ok) {
                showAlert(data.message, 'success');
                setShowDeleteModal(false);
                fetchMedia();
                fetchStats();
            } else {
                showAlert(data.error || 'Failed to delete files', 'danger');
            }
        } catch (error) {
            console.error('Error deleting files:', error);
            showAlert('Failed to delete files', 'danger');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSyncS3 = async (dryRun = true) => {
        setIsSyncing(true);
        try {
            const response = await fetch(`${host}/media/sync-s3`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('token')
                },
                body: JSON.stringify({ dryRun })
            });

            const data = await response.json();
            
            if (response.ok) {
                setSyncResults(data);
                if (!dryRun) {
                    fetchMedia();
                    fetchStats();
                    showAlert(data.message, 'success');
                }
            } else {
                showAlert(data.error || 'Failed to sync with S3', 'danger');
            }
        } catch (error) {
            console.error('Error syncing with S3:', error);
            showAlert('Failed to sync with S3', 'danger');
        } finally {
            setIsSyncing(false);
        }
    };

    const handleSelectFile = (fileId) => {
        setSelectedFiles(prev => 
            prev.includes(fileId) 
                ? prev.filter(id => id !== fileId)
                : [...prev, fileId]
        );
    };

    const handleSelectAll = () => {
        // Select all files that can be marked or unmarked (excluding processing files)
        const selectableFileIds = media.filter(file => 
            ((file.isUnused && !file.markedForDeletion) || file.markedForDeletion) && !processingFiles.has(file._id)
        ).map(file => file._id);
        setSelectedFiles(selectableFileIds);
    };

    const renderFilePreview = (file) => {
        if (file.type === 'image') {
            return (
                <img 
                    src={file.url} 
                    alt="Preview" 
                    style={{ 
                        width: '60px', 
                        height: '60px', 
                        objectFit: 'cover',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    onClick={() => window.open(file.url, '_blank')}
                />
            );
        } else if (file.type === 'pdf') {
            return (
                <div 
                    className="d-flex align-items-center justify-content-center"
                    style={{ 
                        width: '60px', 
                        height: '60px', 
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    onClick={() => window.open(file.url, '_blank')}
                >
                    <i className="fas fa-file-pdf text-danger" style={{ fontSize: '24px' }}></i>
                </div>
            );
        } else {
            return (
                <div 
                    className="d-flex align-items-center justify-content-center"
                    style={{ 
                        width: '60px', 
                        height: '60px', 
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    onClick={() => window.open(file.url, '_blank')}
                >
                    <i className="fas fa-file text-secondary" style={{ fontSize: '24px' }}></i>
                </div>
            );
        }
    };

    const renderUsageInfo = (file) => {
        if (file.usedBy.length === 0) {
            return <Badge bg="warning">Unused</Badge>;
        }

        return (
            <div>
                <Badge bg="success" className="me-1">Used ({file.usedBy.length})</Badge>
                <br />
                <small className="text-muted">
                    {file.usedBy.slice(0, 2).map((usage, index) => (
                        <div key={index}>
                            {usage.studentId?.name || 'Unknown'} ({usage.field})
                        </div>
                    ))}
                    {file.usedBy.length > 2 && (
                        <div>+{file.usedBy.length - 2} more</div>
                    )}
                </small>
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <style>{sortableHeaderStyles}</style>
            <div className="row">
            <Sidedash />
            <div className="col-md-9 pt-3">
                    <div className="row">
                    <h2><i className="fas fa-photo-video me-2"></i>Media Management</h2>
                    <div>
                        <Button 
                            variant="outline-info" 
                            size="sm" 
                            className="me-2"
                            onClick={() => setShowStatsModal(true)}
                        >
                            <i className="fas fa-chart-bar me-1"></i>Stats
                        </Button>
                        <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => setShowSyncModal(true)}
                        >
                            <i className="fas fa-sync-alt me-1"></i>Sync S3
                        </Button>
                    </div>
                </div>

                {/* Statistics Cards */}
                {stats && (
                    <div className="row mb-4 g-3">
                        <div className="col-md-3">
                            <div className="card bg-light text-dark h-100 shadow-sm rounded-4 border-0">
                                <div className="card-body d-flex flex-column justify-content-center align-items-center">
                                    <h5 className="card-title mb-1">{stats.totalFiles}</h5>
                                    <p className="card-text mb-1">Total Files</p>
                                    <small className="text-muted">{stats.totalSizeHuman}</small>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-light text-dark h-100 shadow-sm rounded-4 border-0">
                                <div className="card-body d-flex flex-column justify-content-center align-items-center">
                                    <h5 className="card-title mb-1">{stats.usedFiles}</h5>
                                    <p className="card-text mb-1">Used Files</p>
                                    <small className="text-muted">{stats.usedSizeHuman}</small>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-light text-dark h-100 shadow-sm rounded-4 border-0">
                                <div className="card-body d-flex flex-column justify-content-center align-items-center">
                                    <h5 className="card-title mb-1">{stats.unusedFiles}</h5>
                                    <p className="card-text mb-1">Unused Files</p>
                                    <small className="text-muted">{stats.unusedSizeHuman}</small>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-light text-dark h-100 shadow-sm rounded-4 border-0">
                                <div className="card-body d-flex flex-column justify-content-center align-items-center">
                                    <h5 className="card-title mb-1">{stats.markedForDeletion}</h5>
                                    <p className="card-text mb-1">Marked for Deletion</p>
                                    <small className="text-muted">{stats.markedSizeHuman}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters and Search */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-8">
                                <div className="btn-group" role="group">
                                    {Object.entries(filters).map(([key, count]) => (
                                        <button
                                            key={key}
                                            type="button"
                                            className={`btn ${currentFilter === key ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setCurrentFilter(key)}
                                        >
                                            {key.charAt(0).toUpperCase() + key.slice(1)} ({count})
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search files..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bulk Actions */}
                {selectedFiles.length > 0 && (
                    <div className="alert alert-info">
                        <div className="d-flex justify-content-between align-items-center">
                            <span>{selectedFiles.length} files selected</span>
                            <div>
                                <Button 
                                    variant="warning" 
                                    size="sm" 
                                    className="me-2"
                                    onClick={() => handleMarkForDeletion()}
                                    disabled={processingFiles.size > 0}
                                >
                                    {processingFiles.size > 0 ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </>
                                    ) : 'Mark for Deletion'}
                                </Button>
                                <Button 
                                    variant="success" 
                                    size="sm" 
                                    className="me-2"
                                    onClick={() => handleUnmarkForDeletion()}
                                    disabled={processingFiles.size > 0}
                                >
                                    {processingFiles.size > 0 ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </>
                                    ) : 'Unmark for Deletion'}
                                </Button>
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={() => setSelectedFiles([])}
                                >
                                    Clear Selection
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quick Actions */}
                <div className="card mb-4">
                    <div className="card-body">
                        <h6>Quick Actions</h6>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-warning" 
                                size="sm"
                                onClick={handleSelectAll}
                            >
                                Select All Markable
                            </Button>
                            <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => setShowDeleteModal(true)}
                                disabled={filters.marked === 0}
                            >
                                Delete Marked Files ({filters.marked || 0})
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Media Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th width="50">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedFiles.length === media.filter(f => ((f.isUnused && !f.markedForDeletion) || f.markedForDeletion) && !processingFiles.has(f._id)).length && media.filter(f => ((f.isUnused && !f.markedForDeletion) || f.markedForDeletion) && !processingFiles.has(f._id)).length > 0}
                                                        onChange={handleSelectAll}
                                                    />
                                                </th>
                                                <th width="80">Preview</th>
                                                <th 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('originalName')}
                                                    className="sortable-header"
                                                >
                                                    File Name {getSortIcon('originalName')}
                                                </th>
                                                <th>Type</th>
                                                <th 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('size')}
                                                    className="sortable-header"
                                                >
                                                    Size {getSortIcon('size')}
                                                </th>
                                                <th>Usage</th>
                                                <th 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('uploadedAt')}
                                                    className="sortable-header"
                                                >
                                                    Uploaded {getSortIcon('uploadedAt')}
                                                </th>
                                                <th>Status</th>
                                                <th width="100">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {media.map(file => (
                                                <tr key={file._id} className={file.markedForDeletion ? 'table-warning' : ''}>
                                                    <td>
                                                        {((file.isUnused && !file.markedForDeletion) || file.markedForDeletion) && !processingFiles.has(file._id) && (
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedFiles.includes(file._id)}
                                                                onChange={() => handleSelectFile(file._id)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>{renderFilePreview(file)}</td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                                            <a 
                                                                href={file.url} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="text-decoration-none"
                                                            >
                                                                {file.originalName || file.s3Key}
                                                            </a>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg={file.type === 'image' ? 'primary' : file.type === 'pdf' ? 'danger' : 'secondary'}>
                                                            {file.type.toUpperCase()}
                                                        </Badge>
                                                    </td>
                                                    <td>{file.humanSize}</td>
                                                    <td>{renderUsageInfo(file)}</td>
                                                    <td>
                                                        <small>{new Date(file.uploadedAt).toLocaleDateString()}</small>
                                                    </td>
                                                    <td>
                                                        {file.markedForDeletion && (
                                                            <Badge bg="danger">
                                                                Marked for Deletion
                                                                <br />
                                                                <small>{new Date(file.deletionDate).toLocaleDateString()}</small>
                                                            </Badge>
                                                        )}
                                                        {!file.markedForDeletion && file.isUnused && (
                                                            <Badge bg="warning">Can Delete</Badge>
                                                        )}
                                                        {!file.markedForDeletion && !file.isUnused && (
                                                            <Badge bg="success">In Use</Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {file.isUnused && !file.markedForDeletion && (
                                                            <Button
                                                                variant="outline-warning"
                                                                size="sm"
                                                                onClick={() => handleMarkForDeletion([file._id])}
                                                                disabled={processingFiles.has(file._id)}
                                                            >
                                                                {processingFiles.has(file._id) ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                ) : 'Mark'}
                                                            </Button>
                                                        )}
                                                        {file.markedForDeletion && (
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={() => handleUnmarkForDeletion([file._id])}
                                                                disabled={processingFiles.has(file._id)}
                                                            >
                                                                {processingFiles.has(file._id) ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                ) : 'Unmark'}
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <nav className="mt-3">
                                        <ul className="pagination justify-content-center">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => setCurrentPage(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    Previous
                                                </button>
                                            </li>
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                                                return (
                                                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                                        <button 
                                                            className="page-link" 
                                                            onClick={() => setCurrentPage(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => setCurrentPage(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to permanently delete all files marked for deletion?</p>
                    <Alert variant="warning">
                        <strong>Warning:</strong> This action cannot be undone. Files will be permanently removed from S3.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDeleteMarked}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete Files'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* S3 Sync Modal */}
            <Modal show={showSyncModal} onHide={() => setShowSyncModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Sync with S3</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Sync the database with S3 bucket to find orphaned files.</p>
                    
                    {isSyncing && (
                        <div className="text-center my-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Scanning S3 bucket and comparing with database...</p>
                        </div>
                    )}
                    
                    {syncResults && !isSyncing && (
                        <div className="mb-3">
                            <h6>Sync Results:</h6>
                            <ul>
                                <li>S3 Files: {syncResults.s3FileCount}</li>
                                <li>Database Files: {syncResults.dbFileCount}</li>
                                <li>Orphaned Files: {syncResults.orphanedCount}</li>
                                <li>Missing Files: {syncResults.missingCount}</li>
                            </ul>
                            
                            {syncResults.orphanedFiles.length > 0 && (
                                <div>
                                    <h6>Orphaned Files Found:</h6>
                                    <ul>
                                        {syncResults.orphanedFiles.slice(0, 5).map((file, index) => (
                                            <li key={index}>{file.key} ({(file.size / 1024).toFixed(1)} KB)</li>
                                        ))}
                                        {syncResults.orphanedFiles.length > 5 && (
                                            <li>... and {syncResults.orphanedFiles.length - 5} more</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSyncModal(false)}>
                        Close
                    </Button>
                    <Button 
                        variant="outline-primary" 
                        onClick={() => handleSyncS3(true)}
                        disabled={isSyncing}
                    >
                        {isSyncing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Scanning...
                            </>
                        ) : 'Dry Run'}
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => handleSyncS3(false)}
                        disabled={isSyncing}
                    >
                        {isSyncing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Syncing...
                            </>
                        ) : 'Sync Now'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Stats Modal */}
            <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Media Statistics</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {stats && (
                        <div>
                            <h6>File Distribution:</h6>
                            <ul>
                                <li>Total Files: {stats.totalFiles}</li>
                                <li>Image Files: {stats.imageFiles}</li>
                                <li>PDF Files: {stats.pdfFiles}</li>
                                <li>Other Files: {stats.totalFiles - stats.imageFiles - stats.pdfFiles}</li>
                            </ul>
                            
                            <h6>Usage Statistics:</h6>
                            <ul>
                                <li>Used Files: {stats.usedFiles}</li>
                                <li>Unused Files: {stats.unusedFiles}</li>
                                <li>Marked for Deletion: {stats.markedForDeletion}</li>
                            </ul>
                            
                            <h6>Storage Usage:</h6>
                            <ul>
                                <li>Total Storage: {stats.totalSizeHuman}</li>
                                <li>Unused Storage: {stats.unusedSizeHuman}</li>
                                <li>Storage Efficiency: {((stats.usedFiles / stats.totalFiles) * 100).toFixed(1)}%</li>
                            </ul>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Media; 